import {
	ButtonBlinkMode,
	consumer,
	Delay,
	Execute,
	GameState,
	I18NextProvider,
	IdleStatusProvider,
	inject,
	SequenceChain,
} from "@spawn/core";
import { FreegameActions, FreegameService, FreegameSpinningState } from "@spawn/freegames";
import { GameMessageService, GameMessageType } from "@spawn/modern-ui";
import { enableReel, NudgingService } from "@spawn/nudge";
import { RevealService, RevealState } from "@spawn/reveal";
import {
	AutoplayController,
	CurrencyService,
	NetworkService,
	ReelController,
	SlotMachineButtonId,
	SpinController,
} from "@spawn/slot-machine";
import { GameEvents } from "../events/events";
import { IGameNetworkState } from "../network/networkState";
import { NudgeUpService } from "../services/nudgeUpService";
import { BaseSymbolTypes } from "../services/symbolService";

@consumer
export class FreegameNudgeState extends GameState {
	@inject private readonly revealService!: RevealService;
	@inject private readonly nudgeUpService!: NudgeUpService;
	@inject private readonly nudgingService!: NudgingService;
	@inject private readonly reelController!: ReelController;
	@inject private readonly freegameService!: FreegameService;
	@inject private readonly networkService!: NetworkService;
	@inject private readonly gameMessageService!: GameMessageService;
	@inject private readonly i18n!: I18NextProvider;
	@inject private readonly currencyService!: CurrencyService;
	@inject private readonly idleStatusProvider!: IdleStatusProvider;
	@inject private readonly autoplayController!: AutoplayController;
	@inject private readonly spinController!: SpinController;

	private tempTotalRounds = 0;

	public override async onEnter(): Promise<void> {
		this.spinStateOnEnter();
		this.setButtonStates();

		const offsets = this.nudgingService.getOffsets();
		const nudges = offsets.find((nudge) => nudge !== 0);
		const mysteriesToCover = this.revealService.getMysterySymbols().map((revealTarget) => ({
			position: revealTarget.position,
			symbol: BaseSymbolTypes.Mystery,
			uncoverTier: revealTarget.uncoverTier,
		}));

		if (nudges) {
			this.buttonStateController.setEnabled([SlotMachineButtonId.Spin], true);

			// Check if all reels are being nudged
			const allReelsNudging = offsets.every((offset) => offset !== 0);

			await new SequenceChain(
				new Delay(0.5),
				new Execute(async () => await this.revealService.cover(mysteriesToCover)),
				new Delay(0.5),
				new Execute(async () =>
					// prettier-ignore
					nudges < 0
							? await this.nudgeUpService.performNudgesUp(-nudges)
							: await this.nudgeUpService.performNudgesDown()
				),
				new Execute(async () => {
					if (allReelsNudging) {
						await this.bypassSpin();
					} else {
						this.continueSpin();
					}
				})
			).play();
		} else {
			await this.revealService.cover(mysteriesToCover);
			this.continueSpin();
		}
	}

	private continueSpin(): void {
		this.nudgeUpService.enableNonNudgingReels();
		this.stateComplete(FreegameSpinningState);
	}

	/**
	 * Bypasses the fgspin state (= play all its hooks, spin behind the scenes and go straight to reveal state)
	 * if all reels are being nudged.
	 */
	private async bypassSpin(): Promise<void> {
		this.updateMultiplier();
		this.spinStateOnLeave();

		await this.spinController.spin({ actionType: FreegameActions.FreegameSpin });

		for (let reel = 0; reel < this.reelController.getSlotFace().length; reel++) {
			enableReel(this.reelController, reel);
		}

		this.nudgingService.complete();
		this.stateComplete(RevealState);
	}

	/**
	 * FDM-98: Shows the messages from SpinningState earlier in the "nudging" phase.
	 */
	private spinStateOnEnter(): void {
		this.tempTotalRounds = Number(this.freegameService.state?.totalRounds);

		// Display the total won amount over the course of freegames in the second line.
		// Total win amount will be updated with a wonAmount after the win animation is finished.
		let substractedWonAmount = 0;
		if (this.networkService.resuming) {
			if (!this.freegameService.hasAnyRoundLeft) {
				substractedWonAmount = this.networkService.state.lastWonAmount;
			} else {
				substractedWonAmount = this.networkService.state.wonAmount;
			}
		}
		this.gameMessageService.setCustomGameMessage(
			this.i18n.t("general.totalWin", {
				amount: this.currencyService.getLocalized(
					this.freegameService.state?.totalWinAmount ? this.freegameService.state.totalWinAmount - substractedWonAmount : 0
				),
			}),
			GameMessageType.Secondary
		);

		this.idleStatusProvider.resetIdleTime();

		const currentRound = this.freegameService.state?.currentRound ?? 1;
		const currentTotalRounds =
			this.tempTotalRounds - (this.networkService.resuming ? (this.freegameService.state?.wonFreegames ?? 0) : 0);
		const displayCurrentRound = this.networkService.resuming ? currentRound : currentRound + 1;

		this.setGameMessage(displayCurrentRound, currentTotalRounds);
	}

	private updateMultiplier(): void {
		const offset = this.networkService.resuming ? 0 : 1;
		const multiplier = ((this.networkService.state as IGameNetworkState).slotGameFreeGame?.currentRound ?? 0) + offset;
		this.eventDispatcher.dispatch({
			type: GameEvents.SetMultiplier,
			payload: { multiplier },
		});
	}

	/**
	 * The onLeave hook of the SpinningState.
	 */
	private spinStateOnLeave(): void {
		if (!this.freegameService.isRetrigger) {
			this.setGameMessage(this.freegameService.state?.currentRound ?? 1, this.freegameService.state?.totalRounds ?? 0);
		}

		// Reset the resume after spin is done
		if (this.networkService.resuming) {
			this.networkService.state.session.resumeType = undefined;
		}
	}

	private setGameMessage(currentRound: number, totalRounds: number): void {
		// Display the current round of freegames.
		this.gameMessageService.setCustomGameMessage(
			this.i18n.t("freegames.currentRound", {
				currentRound,
				totalRounds,
			}),
			GameMessageType.Primary
		);
	}

	protected setButtonStates(): void {
		this.buttonStateController.setEnabled(
			[
				SlotMachineButtonId.TurboSpin,
				SlotMachineButtonId.MaxBet,
				SlotMachineButtonId.Bet,
				SlotMachineButtonId.Lines,
				SlotMachineButtonId.QuickMenu,
				SlotMachineButtonId.Help,
				SlotMachineButtonId.PayTable,
			],
			false
		);

		this.buttonStateController.setBlinkMode(SlotMachineButtonId.all, ButtonBlinkMode.None);

		// Casino specific options.
		this.buttonStateController.updateState([SlotMachineButtonId.AutoSpin], {
			isVisible: this.networkService.isAutoPlayAllowed,
			isEnabled: this.autoplayController.isAutoSpinEnabled,
		});
	}
}
